(ns practitest.frontend.project-general-settings.modals
  (:require
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.components.confirmation :as confirmation]
    [practitest.frontend.components.grid.columns-settings :as columns-settings]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.dndkit :as dndkit]
    [practitest.frontend.components2.modal-header :as modal-header]
    [practitest.frontend.project-general-settings.events :as events]
    [practitest.frontend.project-general-settings.subs :as subs]))

(defn remove-logo-modal []
  [confirmation/confirm
   {:show?        (re-frame/subscribe [::subs/remove-logo-confirm?])
    :title        (get-translation "project-settings.general.remove-logo-confirm"
                                   "Are you sure you want to delete the logo?")
    :body         ""
    :yes-label    (get-translation "generic.button.delete" "Delete")
    :no-label     (get-translation "generic.button.cancel" "Cancel")
    :destructive? true
    :test-id      "general-settings-remove-logo-confirm"
    :on-yes       #(re-frame/dispatch [::events/remove-logo])
    :on-no        #(re-frame/dispatch [::events/set-remove-logo-confirm false])}])

(defn- annotation-row [{:keys [id color]}]
  (let [{:keys [setNodeRef listeners active transition transform]} (dndkit/use-sortable id {})]
    [:div.general-settings-annotation-row
     {:ref   setNodeRef
      :class (when (= (:id active) id) "is-dragging")
      :style {:transition transition :transform (dndkit/build-transform-string transform)}}
     [:span.general-settings-annotation-handle listeners [icons/svg-grip-vertical-reduced]]
     [:span.general-settings-annotation-swatch {:style {:background-color color}}]
     [:span.flex-grow-1 id]
     [buttons/icon-button {:icon     [icons/mux-trash]
                           :on-click #(re-frame/dispatch [::events/update-annotation-modal
                                                          events/delete-annotation-type id])}]]))

(defn- annotation-list [types]
  (let [ids (mapv second types)]
    [dndkit/dnd-context
     {:on-drag-end (fn [e]
                     (let [active (some-> e .-active .-id)
                           over   (some-> e .-over .-id)]
                       (when (and active over (not= active over))
                         (re-frame/dispatch [::events/update-annotation-modal update :types
                                             columns-settings/vector-move
                                             (.indexOf ids active) (.indexOf ids over)]))))}
     [dndkit/sortable-context
      {:items ids :strategy dndkit/verticalListSortingStrategy}
      (into [:div.general-settings-annotation-list]
            (for [[color text] types]
              ^{:key text}
              [:f> annotation-row {:id text :color color}]))]]))

(defn annotation-modal []
  (when-let [{:keys [types new-text color]} @(re-frame/subscribe [::subs/annotation-modal])]
    (let [close      #(re-frame/dispatch [::events/close-annotation-modal])
          duplicate? (some #(= (str/lower-case (str/trim new-text)) (str/lower-case (second %))) types)]
      [modal/bootstrap-modal
       {:is-open?         true
        :on-request-close close
        :close-button?    true
        :centered?        true
        :title-as         "div"
        :class            "general-settings-annotation-modal"}
       [modal-header/modal-header
        {:icon     [icons/mux-pencil]
         :title    (get-translation "project-settings.general.annotation-types" "Annotation Types")
         :context  (get-translation "landing.customize" "Customize")
         :subtitle (get-translation "project-settings.general.annotation-types-subtitle"
                                    "The labels testers can add to screenshots during an ET run.")}]
       [:<>
        [:div.general-settings-annotation-new
         [:div.general-settings-field
          [:label.m-label (get-translation "project-settings.general.annotation-new-type" "New Type Name")]
          [:div.d-flex.gap-2
           [:input.m-input
            {:value       new-text
             :class       (when duplicate? "errors")
             :max-length  30
             :placeholder (get-translation "project-settings.general.annotation-placeholder" "e.g. Blocker")
             :on-change   #(re-frame/dispatch [::events/update-annotation-modal assoc :new-text (dom/event-value %)])}]
           [buttons/button {:text      (get-translation "project-settings.general.add-type" "Add Type")
                            :disabled? (or (str/blank? new-text) duplicate?)
                            :on-click  #(re-frame/dispatch [::events/update-annotation-modal events/add-annotation-type])}]]
          (when duplicate?
            [:div.validation-error-message
             (get-translation "project-settings.general.error.annotation-type-exists" "This type already exists")])]
         [:div.general-settings-field
          [:label.m-label (get-translation "dashboard-item.form.gauge-color" "Color")]
          [:div.d-flex.flex-wrap.gap-2
           (for [c events/annotation-colors]
             ^{:key c}
             [:button.general-settings-annotation-color
              {:type     "button"
               :title    c
               :class    (when (= c color) "selected")
               :style    {:background-color (str "var(--" c ")")}
               :on-click #(re-frame/dispatch [::events/update-annotation-modal assoc :color c])}])]]]
        [:div.d-flex.justify-content-between.mt-3.mb-1
         [:span.m-label (get-translation "project-settings.general.types" "Types")]
         [:span.general-settings-hint
          (get-translation "project-settings.general.types-hint" "Drag to reorder. Testers see them in this order.")]]
        [annotation-list types]]
       [:div.d-flex.align-items-center.w-100
        [:span.general-settings-hint.flex-grow-1
         (format-translation (get-translation "project-settings.general.annotation-count" "%{count} annotation types")
                             {:count (count types)})]
        [:div.d-flex.gap-2
         [buttons/button {:text     (get-translation "generic.button.cancel" "Cancel")
                          :on-click close}]
         [buttons/button {:primary? true
                          :text     (get-translation "generic.button.save" "Save")
                          :on-click #(re-frame/dispatch [::events/apply-annotation-types])}]]]])))
